/**
 * Foreign-exchange rates for landed-cost assumptions (HS2-ZJ8P8N). A rate is the amount
 * of the organization's base currency for one unit of a foreign currency, keyed by ISO 4217
 * code. The map replaced the original fixed USD/CNY columns, which are gone (HS2-5QQ5QW).
 */

export const CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/;

export function isCurrencyCode(value: unknown): value is string {
  return typeof value === 'string' && CURRENCY_CODE_PATTERN.test(value);
}

function positive(value: unknown): number | undefined {
  const n = typeof value === 'string' && value !== '' ? Number(value) : value;
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Positive rates from a `{ code: rate }` object (numeric strings allowed); anything else is dropped. */
export function cleanFxRates(value: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return out;
  for (const [code, rate] of Object.entries(value)) {
    const n = positive(rate);
    if (isCurrencyCode(code) && n !== undefined) out[code] = n;
  }
  return out;
}

/** The rates an assumptions-shaped object carries: its `fxRates` map. */
export function effectiveFxRates(source: object | null | undefined): Record<string, number> {
  return cleanFxRates((source as Record<string, unknown> | null | undefined)?.['fxRates']);
}

/** Org default rates overridden, currency by currency, by a per-demand override. */
export function mergeFxRates(
  orgDefault: object | null | undefined,
  demandOverride: object | null | undefined,
): Record<string, number> {
  return { ...effectiveFxRates(orgDefault), ...effectiveFxRates(demandOverride) };
}
